/**
 * CheckboxGroupField component
 */

// Library
import { useEffect, useId, useMemo, useState } from 'react';
import { Text } from '@components';
import { WarningQld } from '@public';
import { v4 as uuidv4 } from 'uuid';

// CSS Style for this module
import styles from './checkboxGroupField.module.scss';

interface CheckGroupOption {
	label: string;
	value: string;
}

/**
 * Interface for CheckboxGroupField props
 */
interface CheckboxGroupFieldProps {
	/**
	 * Custom styles of the button
	 */
	customStyles?: string;
	/**
	 * Value and label pairs as options of the checkbox group
	 */
	options: CheckGroupOption[];
	/**
	 * Name of the field group within a form
	 */
	fieldName: string;
	/**
	 * The default chosen options of the checkbox group
	 */
	defaultValue?: string[];
	/**
	 * onChange function for keeping track field value
	 */
	onChange?: (selectedValues: string[]) => void;
	/**
	 * Helper text for additional guidance about how the input field will be used.
	 * It should only take up a single line
	 */
	hintText?: string;
	/**
	 * Msg to be displayed when the status is error
	 */
	errorMsg?: string;
	/**
	 * Boolean for determining whether this is a disabled field
	 */
	disabled?: boolean;
	/**
	 * Boolean for determining whether this field has to be autofocused
	 */
	autoFocus?: boolean;
}

/**
 * Checkbox field
 * @param {CheckboxGroupFieldProps} Prop
 * @returns an styled checkbox input element
 */
export const CheckboxGroupField = ({
	customStyles,
	options,
	fieldName,
	defaultValue,
	onChange,
	hintText,
	errorMsg,
	disabled = false,
	autoFocus = false,
}: CheckboxGroupFieldProps) => {
	const [optionsIds] = useState(options.map(() => uuidv4()));
	const [checkedValues, setCheckedValues] = useState<string[]>(defaultValue ?? []);
	const hasError = useMemo(() => Boolean(errorMsg), [errorMsg]);
	const internalId = useId();
	const [hintId] = useState(`${internalId}-hint`);
	const [errorId] = useState(`${internalId}-error`);

	useEffect(() => {
		setCheckedValues(defaultValue ?? []);
	}, [defaultValue]);

	const handleCheckboxChange = (value: string) => {
		const currentIndex = checkedValues.indexOf(value);
		const newSelectedValues = [...checkedValues];

		if (currentIndex === -1) {
			newSelectedValues.push(value);
		} else {
			newSelectedValues.splice(currentIndex, 1);
		}

		setCheckedValues(newSelectedValues);
		if (onChange) {
			onChange(newSelectedValues);
		}
	};

	return (
		<div className={[styles.container, customStyles].join(' ')}>
			<div className={styles.textContainer}>
				{hintText && (
					<div>
						<Text variant="muted" id={hintId}>
							{hintText}
						</Text>
					</div>
				)}
				{hasError && (
					<div className={styles.errorContainer}>
						<Text variant="error" id={errorId} role="alert" className={styles.errorContent}>
							<WarningQld customStyles={styles.errorIcon} /> {errorMsg}
						</Text>
					</div>
				)}
			</div>
			<div className={styles['checkbox-group']}>
				{options.map((option, index) => {
					const id = optionsIds[index];
					return (
						<div key={id} className={styles['checkbox-container']}>
							<input
								type="checkbox"
								value={option.value}
								checked={checkedValues.includes(option.value)}
								onChange={() => handleCheckboxChange(option.value)}
								disabled={disabled}
								className={[styles.field, hasError && styles.error].join(' ')}
								role="checkbox"
								name={fieldName}
								autoFocus={autoFocus && index == 0}
								id={id}
								aria-describedby={hintId || errorId ? [hintId, errorId].join(' ') : undefined}
							/>
							<label
								className={[styles.label, disabled && styles['label-disabled']].join(' ')}
								htmlFor={id}
								key={option.value}
							>
								{option.label}
							</label>
						</div>
					);
				})}
			</div>
		</div>
	);
};
